import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";

const starter = `type Listener = (...args: unknown[]) => void;

class EventEmitter {
  on(event: string, listener: Listener): () => void {
    // TODO: register, and return a function that removes this listener
    return () => {};
  }

  once(event: string, listener: Listener): () => void {
    // TODO: like on, but fires at most once
    return () => {};
  }

  off(event: string, listener: Listener): void {
    // TODO
  }

  emit(event: string, ...args: unknown[]): boolean {
    // TODO: call every listener for this event; return whether any ran
    return false;
  }
}
`;

const solution = `type Listener = (...args: unknown[]) => void;

class EventEmitter {
  private listeners = new Map<string, Listener[]>();

  on(event: string, listener: Listener): () => void {
    const list = this.listeners.get(event) ?? [];
    list.push(listener);
    this.listeners.set(event, list);
    // The unsubscribe closes over this exact listener, so it stays correct even
    // if the same function is registered more than once.
    let done = false;
    return () => {
      if (done) return;
      done = true;
      this.off(event, listener);
    };
  }

  once(event: string, listener: Listener): () => void {
    const wrapper: Listener = (...args) => {
      // Remove BEFORE calling. If the listener throws, or re-emits, it must not
      // be able to run a second time.
      this.off(event, wrapper);
      listener(...args);
    };
    return this.on(event, wrapper);
  }

  off(event: string, listener: Listener): void {
    const list = this.listeners.get(event);
    if (!list) return;
    const i = list.indexOf(listener);
    if (i !== -1) list.splice(i, 1);
    if (list.length === 0) this.listeners.delete(event);
  }

  emit(event: string, ...args: unknown[]): boolean {
    const list = this.listeners.get(event);
    if (!list || list.length === 0) return false;
    // Iterate a COPY. A listener that unsubscribes itself mutates the array
    // mid-loop, and iterating the live one silently skips the next listener.
    for (const listener of [...list]) listener(...args);
    return true;
  }
}`;

const harness = HARNESS_KIT + `
if (typeof EventEmitter !== "function") {
  throw new Error("EventEmitter is not defined. Keep the class name as given.");
}

const __tests = [
  {
    name: "on and emit call the listener with the arguments",
    about: "The basic contract: emit forwards every argument to every listener.",
    run: function () {
      const e = new EventEmitter();
      const seen = [];
      e.on("tick", function (a, b) { seen.push([a, b]); });
      e.emit("tick", 1, "two");
      __expectExact("on('tick'); emit('tick', 1, 'two')", function () { return seen; }, [[1, "two"]]);
    }
  },
  {
    name: "emit reports whether anything was listening",
    about: "emit returns true when at least one listener ran, false otherwise.",
    run: function () {
      const e = new EventEmitter();
      __expectExact("emit('nobody')", function () { return e.emit("nobody"); }, false);
      e.on("x", function () {});
      __expectExact("on('x'); emit('x')", function () { return e.emit("x"); }, true);
    }
  },
  {
    name: "listeners for other events are not called",
    about: "Events are separate channels; emitting one must not wake another.",
    run: function () {
      const e = new EventEmitter();
      let other = 0;
      e.on("a", function () { other++; });
      e.emit("b");
      __expectExact("on('a'); emit('b')", function () { return other; }, 0);
    }
  },
  {
    name: "the function returned by on removes that listener",
    about: "on returns an unsubscribe, which is the ergonomic most callers use.",
    run: function () {
      const e = new EventEmitter();
      let n = 0;
      const off = e.on("go", function () { n++; });
      e.emit("go");
      off();
      e.emit("go");
      __expectExact("off(); emit('go')", function () { return n; }, 1);
    }
  },
  {
    name: "once fires exactly once",
    hidden: true,
    about: "A once listener must be removed before it is called, not after.",
    run: function () {
      const e = new EventEmitter();
      let n = 0;
      e.once("boot", function () { n++; });
      e.emit("boot");
      e.emit("boot");
      e.emit("boot");
      __expectExact("once('boot'); emit x3", function () { return n; }, 1);
    }
  },
  {
    name: "a listener unsubscribing during emit does not skip the next one",
    hidden: true,
    about: "The classic bug. off() splices the array the loop is walking, so index i+1 is skipped. Iterate a copy. This passes every naive test and fails only when a listener removes itself, which is exactly what once does.",
    run: function () {
      const e = new EventEmitter();
      const called = [];
      const offA = e.on("go", function () { called.push("a"); offA(); });
      e.on("go", function () { called.push("b"); });
      e.on("go", function () { called.push("c"); });
      e.emit("go");
      __expectExact("first listener removes itself during emit", function () { return called; }, ["a", "b", "c"]);
    }
  },
  {
    name: "once combined with a plain listener still calls both",
    hidden: true,
    about: "once removes itself mid-emit, which is the same mutation hazard from the inside.",
    run: function () {
      const e = new EventEmitter();
      const called = [];
      e.once("go", function () { called.push("once"); });
      e.on("go", function () { called.push("always"); });
      e.emit("go");
      e.emit("go");
      __expectExact("once + on, emitted twice", function () { return called; }, ["once", "always", "always"]);
    }
  },
  {
    name: "the same listener registered twice runs twice",
    hidden: true,
    about: "Registrations are a list, not a set. Deduplicating silently loses a subscription the caller asked for.",
    run: function () {
      const e = new EventEmitter();
      let n = 0;
      const fn = function () { n++; };
      e.on("x", fn);
      e.on("x", fn);
      e.emit("x");
      __expectExact("same function registered twice", function () { return n; }, 2);
    }
  },
  {
    name: "off removes only one of two identical registrations",
    hidden: true,
    about: "Removing every copy would unsubscribe a caller that never asked to be unsubscribed.",
    run: function () {
      const e = new EventEmitter();
      let n = 0;
      const fn = function () { n++; };
      e.on("x", fn);
      e.on("x", fn);
      e.off("x", fn);
      e.emit("x");
      __expectExact("registered twice, off once", function () { return n; }, 1);
    }
  },
  {
    name: "unsubscribing twice is harmless",
    hidden: true,
    about: "Cleanup runs on unmount and can run again. The second call must not remove somebody else's listener.",
    run: function () {
      const e = new EventEmitter();
      let n = 0;
      const off = e.on("x", function () {});
      e.on("x", function () { n++; });
      off();
      off();
      e.emit("x");
      __expectExact("off() called twice", function () { return n; }, 1);
    }
  },
  {
    name: "off for an unknown event or listener does not throw",
    hidden: true,
    about: "Defensive callers call off on things that were never registered.",
    run: function () {
      const e = new EventEmitter();
      __expectExact("off on an empty emitter", function () {
        e.off("nope", function () {});
        return "survived";
      }, "survived");
    }
  },
  {
    name: "adding a listener during emit does not run it in the same emit",
    hidden: true,
    about: "A listener registered while the event is already firing belongs to the next emit. Running it immediately can loop forever.",
    run: function () {
      const e = new EventEmitter();
      const called = [];
      e.on("go", function () {
        called.push("first");
        e.on("go", function () { called.push("added"); });
      });
      e.emit("go");
      __expectExact("listener added during emit", function () { return called; }, ["first"]);
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "A listener is allowed to remove itself while you are part way through notifying everyone. Ask what splice does to the array your loop is indexing, and which listener never gets called as a result.",
    code: `emit(event: string, ...args: unknown[]): boolean {
  const list = this.listeners.get(event);
  if (!list || list.length === 0) return false;

  // Iterate a COPY. A listener that unsubscribes itself mutates the real array
  // mid-loop, and walking the live one silently skips the next listener.
  for (const listener of [...list]) listener(...args);
  return true;
}`,
  },
  {
    label: "Approach",
    body: "A Map of event name to a list of listeners, and everything else falls out of it. on pushes and hands back a closure over that exact listener. off finds it by identity and splices one copy, not all of them. once is on with a wrapper that removes itself first.",
    code: `on(event: string, listener: Listener): () => void {
  const list = this.listeners.get(event) ?? [];
  list.push(listener);
  this.listeners.set(event, list);
  let done = false;
  return () => {                      // idempotent: cleanup can run twice
    if (done) return;
    done = true;
    this.off(event, listener);
  };
}

once(event: string, listener: Listener): () => void {
  const wrapper: Listener = (...args) => {
    this.off(event, wrapper);         // remove BEFORE calling, in case it throws or re-emits
    listener(...args);
  };
  return this.on(event, wrapper);
}

off(event: string, listener: Listener): void {
  const list = this.listeners.get(event);
  if (!list) return;
  const i = list.indexOf(listener);
  if (i !== -1) list.splice(i, 1);    // one copy, so registering twice and off-ing once leaves one
}`,
  },
  {
    label: "The trap",
    body: "once is the case that removes a listener mid-emit, so the mutation hazard is not exotic, it is the normal path. The copy also gives you the other rule for free: a listener added during an emit belongs to the next emit, not the one that added it, which is what stops an emitter looping forever.",
  },
];

export const eventEmitter: Drill = {
  id: "event-emitter",
  track: "typescript",
  title: "Event Emitter",
  pattern: "pubsub",
  difficulty: "medium",
  basePoints: 250,
  timeBudgetMs: 25 * 60 * 1000,
  statement: `Every gateway, socket client and plugin system in the world has one of these at the bottom of it. Implement \`EventEmitter\`: callers subscribe to named events, and something else emits them.

## The API

\`\`\`ts
class EventEmitter {
  on(event: string, listener: Listener): () => void;   // returns an unsubscribe
  once(event: string, listener: Listener): () => void;
  off(event: string, listener: Listener): void;
  emit(event: string, ...args: unknown[]): boolean;    // true if anything ran
}
\`\`\`

## Requirements

- **\`emit\` forwards every argument** to every listener registered for that event, in the order they were added.
- **\`emit\` returns** whether at least one listener ran.
- **\`on\` returns an unsubscribe function** as well as being removable via \`off\`.
- **\`once\` fires at most once**, then removes itself.
- **The same listener may be registered twice**, and then runs twice. \`off\` removes one registration, not both.

## Example

\`\`\`ts
const bus = new EventEmitter();
const off = bus.on("message", (text) => console.log("got", text));

bus.emit("message", "hello");   // logs: got hello   → returns true
off();
bus.emit("message", "hello");   // nothing listening → returns false
\`\`\`

## Where this gets interesting

A listener is allowed to unsubscribe itself while the event is being emitted. That is not an exotic case, it is exactly what \`once\` does. Think about what that does to a loop walking the listener list, and about what should happen to a listener added *during* an emit.`,
  hints,
  starter,
  harness,
  solution,
  complexity:
    "O(1) to subscribe, O(n) to emit over n listeners, O(n) to remove one by identity. A Map of event name to array is the shape; the copy in emit is the part that matters.",
};
